import { use_slice_generation } from '@/hooks/store/use_slices';
import { CONSTANTS__shared } from '@shared/constants/CONSTANTS__shared';
import { MAP__serializable } from '@shared/dto/_base/MAP__serializable';
import { ENUM__field_graph_interaction } from '@shared/dto/generation/graph_invoke/_fields/_base/_enums/ENUM__field_graph_interaction';
import { FN_FIELD__get_fields_map_with_graph_interaction } from '@shared/dto/generation/graph_invoke/_fields/_base/_methods/FN_FIELD__get_fields_map_with_graph_interaction';
import { FIELD__base } from '@shared/dto/generation/graph_invoke/_fields/_base/FIELD__base';
import { FIELD__list_base_generic } from '@shared/dto/generation/graph_invoke/_fields/_primitives/list/FIELD__list_base_generic';
import { FIELD__list_image_asset } from '@shared/dto/generation/graph_invoke/_fields/_primitives/list/FIELD__list_image_asset';
import { INVOCATION__base } from '@shared/dto/generation/graph_invoke/_invocations/_base/INVOCATION__base';
import { MAP__fields_by_id } from '@shared/types/types';
import { useMemo } from 'react';
import { G_GRAPH_FIELD__row } from './fields/G_GRAPH_FIELD__row';
import { G_GRAPH_FIELD__row_list } from './fields/G_GRAPH_FIELD__row_list';
import { G_GRAPH_NODE__table_wrapper } from './G_GRAPH_NODE__table_wrapper';

interface PROPS {
  title: string;
  fields_map: MAP__fields_by_id;
  node: INVOCATION__base;
}

export function G_GRAPH_NODE__invocation_fields({ title, fields_map, node }: PROPS) {
  /*
   * STORE
   */
  const { generation_current } = use_slice_generation(['generation_current']);
  const fields_map_all = generation_current.tool.graph.fields__get_map();

  const fields_map_to_show = useMemo(() => get_fields_map_to_show(), [fields_map, generation_current.tool.graph]);

  /*
   * METHODS
   */
  function get_fields_map_to_show() {
    if (node.meta.show_hidden_fields) {
      let out = remove_items_of_lists_from_map(fields_map);
      return out;
    }

    let target_interaction = [ENUM__field_graph_interaction.VISIBLE_CONNECTABLE, ENUM__field_graph_interaction.VISIBLE_NON_CONNECTABLE];

    let out: MAP__fields_by_id = new MAP__serializable();

    for (let [field_id, field] of fields_map.entries()) {
      if (!target_interaction.includes(field.meta.graph__interaction)) continue;

      out.set(field_id, field);
    }

    out = remove_items_of_lists_from_map(out);

    return out;
  }

  function remove_items_of_lists_from_map(target_map: MAP__fields_by_id) {
    let target_interaction = [ENUM__field_graph_interaction.VISIBLE_CONNECTABLE, ENUM__field_graph_interaction.VISIBLE_NON_CONNECTABLE];

    let out = target_map.clone();

    let lists = node.fields__get_by_type(FIELD__list_base_generic);
    let list_items = lists.flatMap(list => list.items).map(item => item);

    let connectable_fields_all: MAP__fields_by_id = new MAP__serializable();

    for (let list_item of list_items) {
      let connectable_fields_item = FN_FIELD__get_fields_map_with_graph_interaction(list_item, target_interaction, true);

      connectable_fields_all.merge_with(connectable_fields_item);
    }

    let list_items_ids = connectable_fields_all.values().map(item => item.meta.field_id);

    for (let list_item_id of list_items_ids) {
      out.delete(list_item_id);
    }

    return out;
  }

  /*
   * RENDERS
   */

  function render_field_row(field: FIELD__base) {
    let out = <></>;

    if (!fields_map_all.has(field.meta.field_id)) return null; //field deleted

    let field_parent = generation_current.tool.graph.field__get_parent(field.meta);

    if (field instanceof FIELD__list_image_asset) {
      out = <G_GRAPH_FIELD__row key={field.meta.field_id} {...{ field, field_parent }} />;
    } else if (field instanceof FIELD__list_base_generic) {
      out = <G_GRAPH_FIELD__row_list key={field.meta.field_id} {...{ field, field_parent }} />;
    } else {
      out = <G_GRAPH_FIELD__row key={field.meta.field_id} {...{ field, field_parent }} />;
    }

    return out;
  }

  /*
   * HTML
   */

  if (fields_map_to_show.size() === 0) return null;

  return (
    <G_GRAPH_NODE__table_wrapper {...{ title }}>
      <div style={{ paddingBottom: `${CONSTANTS__shared.GRID_CELL_SIZE}px` }}>{fields_map_to_show.values().map(render_field_row)}</div>
    </G_GRAPH_NODE__table_wrapper>
  );
}
